import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Wallet } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import {
  fetchUserWallet,
  fetchUserWalletTxns,
  type AdminUserWallet,
  type AdminWalletTxn,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { RelativeTime } from '@/components/DataDisplay'
import { Skeleton } from '@/components/States'

/**
 * A3.15 / A10.04 — Foydalanuvchi hamyoni. GET /admin/users/:id/wallet (backend `f5fe3cb`).
 * Balans = ichki Take token (BIGINT). Ledger tranzaksiyalari jadvalda.
 */
export function UserWallet() {
  const t = useT()
  const { id = '' } = useParams()
  const [wallet, setWallet] = useState<AdminUserWallet | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchUserWallet(Number(id), ac.signal)
      .then((w) => {
        if (alive) setWallet(w)
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id])

  const columns = useMemo<AdminColumn<AdminWalletTxn>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
      {
        key: 'delta',
        header: t.finance.amount,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => (
          <span className={cn('block text-right font-mono tabular', r.delta >= 0 ? 'text-success' : 'text-danger')}>
            {r.delta >= 0 ? '+' : ''}
            {r.delta.toLocaleString()}
          </span>
        ),
      },
      { key: 'kind', header: t.col.type, type: 'text', width: 160 },
      { key: 'note', header: t.finance.note, type: 'longtext', minWidth: 180 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserWalletTxns(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.users.wallet}
        screenId="A3.15"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
          <Wallet size={20} className="text-accent" />
          <div>
            <div className="text-xs text-tertiary">{t.finance.balance}</div>
            {loading ? (
              <Skeleton className="mt-1 h-6 w-24" />
            ) : (
              <div className="font-mono text-xl tabular text-primary">
                {(wallet?.balance ?? 0).toLocaleString()}
              </div>
            )}
          </div>
          {wallet?.updated_at ? (
            <div className="ml-4 text-xs text-tertiary">
              {t.finance.updated}: <RelativeTime value={wallet.updated_at} />
            </div>
          ) : null}
        </div>
      </div>
      <DataTable<AdminWalletTxn>
        screenId="A3.15"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Wallet size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
